import { useEffect, useState } from "react";
import { ChartCard, Skeleton } from "@/components/reusable";
import { fetchNavZoneExposure } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { NavZoneExposureRow, Severity } from "@/types/analytics";

function usdCompact(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${Math.round(n).toLocaleString()}`;
}

const BADGE_CLASS: Record<Severity, string> = {
  danger: "bg-[var(--color-error-light)] text-[#791F1F]",
  warn: "bg-[var(--color-warning-light)] text-[#854F0B]",
  info: "bg-[#E6F1FB] text-[#0C447C]",
  neutral: "bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]",
  ok: "bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary-dark)]",
};

export function WarRiskExposure() {
  const filters = useAnalyticsFilters();
  const [rows, setRows] = useState<NavZoneExposureRow[] | null>(null);

  useEffect(() => {
    let active = true;
    setRows(null);
    fetchNavZoneExposure(filters).then((r) => {
      if (active) setRows(r);
    });
    return () => {
      active = false;
    };
  }, [filters]);

  const flagged = rows?.filter((r) => r.zoneKey !== "worldwide" && r.vesselCount > 0) ?? [];
  const flaggedTotal = flagged.reduce((a, r) => a + r.vesselCount, 0);

  return (
    <ChartCard
      title="War risk & navigational limit exposure"
      subtitle="Vessels with declared limits in elevated-risk trading zones"
      headerRight={
        rows ? (
          <span
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${BADGE_CLASS[flaggedTotal > 0 ? "danger" : "neutral"]}`}
          >
            {flaggedTotal} vessels flagged
          </span>
        ) : null
      }
    >
      {!rows ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height="h-4" />
          ))}
        </div>
      ) : (
        <ul>
          {rows.map((r) => (
            <li
              key={r.zoneKey}
              className="flex items-center justify-between gap-3 py-2 border-b border-[var(--color-border-light)] last:border-b-0"
            >
              <div className="min-w-0">
                <div className="text-[12px] font-medium text-[var(--color-text-primary)]">
                  {r.zoneLabel}
                </div>
                <div className="text-[11px] text-[var(--color-text-tertiary)] mt-0.5">
                  {r.description}
                </div>
              </div>
              <div className="flex items-center gap-2.5 flex-shrink-0">
                <span
                  className="text-[11px] text-[var(--color-text-secondary)]"
                  style={{ fontFamily: "var(--font-mono)" }}
                >
                  {usdCompact(r.siUsd)} SI
                </span>
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${BADGE_CLASS[r.severity]}`}
                >
                  {r.vesselCount} vessel{r.vesselCount === 1 ? "" : "s"}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </ChartCard>
  );
}
